import { differenceInCalendarDays, parseISO } from 'date-fns';
import { rechainTaper } from '@/common/medicationSchedule';
import { taperStepDose, taperStepDuration } from '@/common/medicationTaper';
import { hasMedicationReason } from '@/common/medicationPreferences';
import { blankWindow, holdReasonConceptOf, intervalLabel, reasonValueOf, unitName, windowSegmentInput } from '@/common/medicationWindow';
import { administrationTypeOptions, DOSE_UNIT_CODES } from '@/composables/useMedicationOrderEditor';

/**
 * Medication list presets: a named list of medication orders kept for the account and copied by
 * value onto a patient when applied. A preset states no administration times, days of week,
 * dates or site — those are supplied when it is applied.
 *
 * A Taper item's step 1 is the item's own order, as the patient taper's step 1 is the
 * medication's order: the item keeps dose, frequency and interval, and taperStep[0] never stores
 * its own.
 */

export const RXNORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm';

export const PRESET_ADMINISTRATION_TYPES = ['scheduled', 'prn', 'taper'];

const UCUM_SYSTEM = 'http://unitsofmeasure.org';
const STEP_TYPES = ['scheduled', 'prn', 'hold'];
const INTERVAL_UNITS = ['d', 'wk'];
const DURATION_UNITS = ['d', 'wk', 'mo'];

// Fields a preset never carries; removed defensively wherever a step or item is normalised.
const APPLY_TIME_FIELDS = ['times', 'timeOfDay', 'dayOfWeek', 'startDay', 'endDay', 'startDate', 'boundsPeriod', 'administeringLocation'];

// Any fixed day works: spans are offsets from day 1, and rechainTaper only counts days.
const SPAN_ANCHOR = '2000-01-03';

const isPositive = (value) => Number(value) > 0;
const isWholeAtLeastOne = (value) => Number.isInteger(Number(value)) && Number(value) >= 1;
const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';
const toJson = (value) => JSON.parse(JSON.stringify(typeof value?.toJSON === 'function' ? value.toJSON() : value || {}));

/**
 * Whether a medication is a coded RxNorm concept rather than free text.
 *
 * @param {object} [concept] - a CodeableConcept
 * @returns {boolean}
 */
export const isCodedRxNorm = (concept) => (concept?.coding || []).some((coding) => coding?.system === RXNORM_SYSTEM && !isBlank(coding?.code));

/**
 * A picked search result as the medication a preset item stores.
 *
 * @param {{code: string, display: string}} result
 * @returns {object} CodeableConcept
 */
export const presetMedicationFrom = ({ code, display } = {}) => ({ text: display, coding: [{ system: RXNORM_SYSTEM, code, display }] });

/**
 * A dose amount and unit as the Quantity an item stores. A unit typed by hand is carried as a
 * display unit alone rather than as an invented code.
 *
 * @param {*} value
 * @param {string} [unit]
 * @returns {object|undefined}
 */
export const presetDoseQuantity = (value, unit) => {
    const amount = isBlank(value) ? NaN : Number(value);
    const hasAmount = Number.isFinite(amount) && amount > 0;
    const text = String(unit ?? '').trim();
    if (!hasAmount && !text) return undefined;

    const quantity = {};
    if (hasAmount) quantity.value = amount;
    if (text) {
        quantity.unit = text;
        const code = DOSE_UNIT_CODES[text];
        if (code) {
            quantity.system = UCUM_SYSTEM;
            quantity.code = code;
        }
    }
    return quantity;
};

/**
 * A PRN minimum interval as the Duration an item stores.
 *
 * @param {*} value
 * @param {'h'|'min'} [code='h']
 * @returns {object|undefined}
 */
export const presetMinimumInterval = (value, code = 'h') => {
    if (!isPositive(value)) return undefined;
    const unitCode = code || 'h';
    return { value: Number(value), unit: unitCode === 'h' ? 'hours' : 'minutes', system: UCUM_SYSTEM, code: unitCode };
};

/**
 * The administration types a preset item offers. Never Unknown — a preset is an order. Taper only
 * where the eMAR rollout flag is on, or where the item already is one, so turning the flag off
 * never converts or drops a saved taper.
 *
 * @param {{emarEnabled: boolean, currentType?: string}} options
 * @returns {Array<{value: string, title: string}>}
 */
export const presetAdministrationTypeOptions = ({ emarEnabled, currentType } = {}) =>
    administrationTypeOptions
        .filter((option) => option.value !== 'unknown')
        .filter((option) => option.value !== 'taper' || emarEnabled || currentType === 'taper')
        .map(({ value, title }) => ({ value, title }));

/**
 * A stored taper step as the window BnMedicationWindowFields and the taper editor work in.
 * Step 1 reads its dose, frequency and interval off the item, which is its order.
 *
 * @param {object} step - BN_MedicationPresetTaperStep JSON
 * @param {number} index
 * @param {object} item - BN_MedicationPresetItem JSON
 * @returns {object}
 */
export const presetStepToWindow = (step, index, item) => {
    const source = index === 0 ? item : step;
    const window = blankWindow({
        type: step?.type || 'scheduled',
        period: isPositive(source?.period) ? Number(source.period) : 1,
        periodUnit: source?.periodUnit || 'd',
        frequency: isPositive(source?.frequency) ? Number(source.frequency) : null,
        // The preset editor no longer offers or writes a window (Jayte, 2026-10-23); one an older
        // preset stored is still read, so applying it is unchanged. Blank is the account's window.
        administrationWindow: step?.administrationWindow ?? null,
        selfAdministered: step?.selfAdministered === true,
        maxPerDay: step?.maxPerDay ?? null,
        minimumIntervalValue: step?.minimumInterval?.value ?? null,
        minimumIntervalUnit: step?.minimumInterval?.code || 'h',
        prnIndication: step?.prnIndication || '',
        holdReasonCode: reasonValueOf(step?.holdReason),
        holdNotes: step?.holdNotes || '',
    });
    window.doseValue = (index === 0 ? item?.dose?.value : step?.dose?.value) ?? null;
    window.durationValue = step?.durationValue ?? null;
    window.durationUnit = step?.durationUnit || 'd';
    return window;
};

/**
 * A step window as the taper step a preset stores. Only the fields its own type owns; never
 * times, days of week, dates or a site.
 *
 * @param {object} window
 * @param {number} index
 * @param {{itemDose?: object, unbounded?: boolean, holdReasonConcepts?: Array}} [options]
 * @returns {object} BN_MedicationPresetTaperStep JSON
 */
export const presetStepFromWindow = (window, index, { itemDose, unbounded = false, holdReasonConcepts = [] } = {}) => {
    const type = window?.type || 'scheduled';
    const step = { type };

    if (index > 0) {
        const dose = taperStepDose(window?.doseValue, itemDose);
        if (dose) step.dose = dose;
        if (type === 'scheduled') {
            if (isPositive(window?.frequency)) step.frequency = Number(window.frequency);
            step.period = isPositive(window?.period) ? Number(window.period) : 1;
            step.periodUnit = window?.periodUnit || 'd';
        }
    }

    const duration = taperStepDuration(window, { unbounded });
    if (duration) {
        step.durationValue = duration.value;
        step.durationUnit = duration.code;
    } else if (!unbounded) {
        // The unit the step shows is the unit it has, even before Continue for is entered, so a
        // step is never told to choose a unit it is already showing.
        step.durationUnit = DURATION_UNITS.includes(window?.durationUnit) ? window.durationUnit : 'd';
    }

    const input = windowSegmentInput(window);
    // No administration window: a preset step takes the account's when the preset is applied.
    if (type === 'scheduled' && input.selfAdministered === true) step.selfAdministered = true;
    if (type === 'prn') {
        if (input.maxPerDay !== undefined) step.maxPerDay = input.maxPerDay;
        if (input.minimumInterval) step.minimumInterval = input.minimumInterval;
        if (!isBlank(window?.prnIndication)) step.prnIndication = String(window.prnIndication).trim();
    }
    if (type === 'hold') {
        const reason = holdReasonConceptOf(holdReasonConcepts, window?.holdReasonCode);
        if (reason) step.holdReason = reason;
        if (!isBlank(window?.holdNotes)) step.holdNotes = String(window.holdNotes).trim();
    }
    return step;
};

const formatSpan = (first, last, weeks, onward) => {
    const unit = weeks ? 'Week' : 'Day';
    const toUnit = (day) => (weeks ? (day - 1) / 7 + 1 : day);
    const start = toUnit(first);
    if (onward) return `${unit} ${start} onward`;
    const end = weeks ? last / 7 : last;
    return start === end ? `${unit} ${start}` : `${unit}s ${start}–${end}`;
};

/**
 * Each step's span counted from day 1, by running the real rechainTaper over the steps'
 * durations — so a span is exactly the offsets the step gets when the preset is applied. A month
 * is 30 days there, so a span is labelled in days unless every step so far is whole weeks.
 *
 * @param {Array<object>} steps - BN_MedicationPresetTaperStep JSON (or windows)
 * @param {'stop'|'continue'} [afterFinal]
 * @returns {string[]} one label per step, '' when not computable
 */
export const presetTaperSpans = (steps, afterFinal) => {
    const list = Array.isArray(steps) ? steps : [];
    const labels = list.map(() => '');
    try {
        const last = list.length - 1;
        const shells = [];
        for (let index = 0; index < list.length; index++) {
            const onward = index === last && afterFinal === 'continue';
            const stepDuration = taperStepDuration(list[index], { unbounded: onward });
            // Nothing after a step with no length has anywhere to start.
            if (!stepDuration && !onward) break;
            shells.push({ stepDuration });
        }
        if (!shells.length) return labels;

        const chained = rechainTaper(shells, SPAN_ANCHOR);
        const anchor = parseISO(SPAN_ANCHOR);
        let weeksSoFar = true;
        chained.forEach((dosage, index) => {
            const bounds = dosage?.timing?.repeat?.boundsPeriod;
            const first = differenceInCalendarDays(parseISO(bounds.start), anchor) + 1;
            const onward = !bounds.end;
            const last = onward ? null : differenceInCalendarDays(parseISO(bounds.end), anchor) + 1;
            // Weeks only while every step so far was entered in Week(s): a span reads in the unit
            // the user picked, so seven Day(s) stays "Days 1–7" (Jayte, 2026-10-02).
            if (!onward && list[index]?.durationUnit !== 'wk') weeksSoFar = false;
            labels[index] = formatSpan(first, last, weeksSoFar, onward);
        });
    } catch {
        return labels;
    }
    return labels;
};

const stripApplyTimeFields = (json) => APPLY_TIME_FIELDS.forEach((field) => delete json[field]);

const STEP_TYPE_FIELDS = {
    scheduled: ['selfAdministered'],
    prn: ['maxPerDay', 'minimumInterval', 'prnIndication'],
    hold: ['holdReason', 'holdNotes'],
};

const normalizePresetStep = (input, index, { final, afterFinal, itemDose }) => {
    const step = { ...input };
    stripApplyTimeFields(step);
    // Never stated by a preset step; the account's window is used when the preset is applied.
    delete step.administrationWindow;
    step.type = STEP_TYPES.includes(step.type) ? step.type : 'scheduled';

    if (index === 0) {
        delete step.dose;
        delete step.frequency;
        delete step.period;
        delete step.periodUnit;
    } else {
        const dose = taperStepDose(step.dose?.value, itemDose);
        if (dose) step.dose = dose;
        else delete step.dose;
        if (step.type !== 'scheduled') {
            delete step.frequency;
            delete step.period;
            delete step.periodUnit;
        }
    }

    Object.entries(STEP_TYPE_FIELDS).forEach(([type, fields]) => {
        if (type !== step.type) fields.forEach((field) => delete step[field]);
    });
    if (step.selfAdministered !== true) delete step.selfAdministered;
    if (isBlank(step.prnIndication)) delete step.prnIndication;
    if (isBlank(step.holdNotes)) delete step.holdNotes;

    if (final && afterFinal === 'continue') {
        delete step.durationValue;
        delete step.durationUnit;
    }
    return step;
};

/**
 * The item as its type stores it: fields another type owns are removed (absent, never null or
 * ''), so a PRN item never carries a stray frequency and a Scheduled item never keeps taper steps.
 *
 * @param {object} item - BN_MedicationPresetItem or its JSON
 * @param {'scheduled'|'prn'|'taper'} type
 * @returns {object} item JSON
 */
export const normalizePresetItem = (item, type) => {
    const json = toJson(item);
    stripApplyTimeFields(json);
    json.administrationType = type;

    if (type !== 'taper') {
        delete json.taperStep;
        delete json.afterFinal;
        delete json.taperNotes;
    }
    if (type === 'prn') {
        delete json.frequency;
        delete json.period;
        delete json.periodUnit;
    } else {
        delete json.maxPerDay;
        delete json.minimumInterval;
    }

    if (type === 'taper') {
        json.afterFinal = json.afterFinal === 'continue' ? 'continue' : 'stop';
        const steps = Array.isArray(json.taperStep) ? json.taperStep : [];
        json.taperStep = steps.map((step, index) => normalizePresetStep(step, index, { final: index === steps.length - 1, afterFinal: json.afterFinal, itemDose: json.dose }));
        if (isBlank(json.taperNotes)) delete json.taperNotes;
    }

    if (isBlank(json.patientInstruction)) delete json.patientInstruction;
    if (isBlank(json.route?.text) && !json.route?.coding?.length) delete json.route;
    if (Array.isArray(json.reasonCode)) {
        json.reasonCode = json.reasonCode.filter((reason) => hasMedicationReason([reason]));
        if (!json.reasonCode.length) delete json.reasonCode;
    }
    return json;
};

const hasHoldReason = (reason) => Boolean(reason && (!isBlank(reason.text) || (reason.coding || []).some((coding) => !isBlank(coding?.code))));

const orderErrors = (item, errors, key = (field) => field) => {
    if (!isPositive(item?.dose?.value)) errors[key('dose')] = 'Enter a dose greater than 0';
    if (!isWholeAtLeastOne(item?.frequency)) errors[key('frequency')] = 'Enter how many times, 1 or more';
    if (!isPositive(item?.period)) errors[key('period')] = 'Enter an interval greater than 0';
    if (!INTERVAL_UNITS.includes(item?.periodUnit)) errors[key('periodUnit')] = 'Choose day(s) or week(s)';
};

const prnErrors = (source, errors, key = (field) => field) => {
    if (!isWholeAtLeastOne(source?.maxPerDay)) errors[key('maxPerDay')] = 'Enter the maximum times per day, 1 or more';
    if (!isPositive(source?.minimumInterval?.value)) errors[key('minimumInterval')] = 'Enter a minimum interval greater than 0';
};

/**
 * What stops an item being saved. Step errors are keyed 'taperStep.{i}.{field}'. The titration
 * acknowledgement is not checked here: it is never stored, and is asked every time.
 *
 * @param {object} item
 * @param {{requireReason?: boolean}} options - requireReason: the account requires a reason for taking medication
 * @returns {Record<string, string>} {} when valid
 */
export const presetItemErrors = (item, { requireReason = false } = {}) => {
    const errors = orderItemErrors(item);
    if (requireReason && !hasMedicationReason(item?.reasonCode)) errors.reasonCode = 'Enter a reason for taking medication';
    return errors;
};

const orderItemErrors = (item) => {
    const errors = {};
    if (!isCodedRxNorm(item?.medication)) errors.medication = 'Choose a medication from the search results';

    const type = item?.administrationType;
    if (!PRESET_ADMINISTRATION_TYPES.includes(type)) {
        errors.administrationType = 'Choose how this medication is given';
        return errors;
    }

    if (type === 'prn') {
        if (!isPositive(item?.dose?.value)) errors.dose = 'Enter a dose greater than 0';
        prnErrors(item, errors);
        return errors;
    }

    orderErrors(item, errors);
    if (type !== 'taper') return errors;

    const steps = Array.isArray(item.taperStep) ? item.taperStep : [];
    if (!steps.length) errors.taperStep = 'Add at least one step';
    if (item.afterFinal !== undefined && item.afterFinal !== null && !['stop', 'continue'].includes(item.afterFinal)) errors.afterFinal = 'Choose what happens after the final step';

    steps.forEach((step, index) => {
        const key = (field) => `taperStep.${index}.${field}`;
        const stepType = step?.type || 'scheduled';
        if (!STEP_TYPES.includes(stepType)) errors[key('type')] = 'Choose scheduled, PRN or hold';

        const openEnded = index === steps.length - 1 && item.afterFinal === 'continue';
        if (!openEnded) {
            if (!isPositive(step?.durationValue)) errors[key('durationValue')] = 'Enter how long this step continues';
            if (!DURATION_UNITS.includes(step?.durationUnit)) errors[key('durationUnit')] = 'Choose day(s), week(s) or month(s)';
        }

        if (stepType === 'scheduled' && index > 0) orderErrors(step, errors, key);
        if (stepType === 'prn') prnErrors(step, errors, key);
        if (stepType === 'hold' && !hasHoldReason(step?.holdReason)) errors[key('holdReason')] = 'Choose a hold reason';
    });
    return errors;
};

/**
 * What stops a preset being saved. Names are not checked for uniqueness.
 *
 * @param {object} preset
 * @param {{requireReason?: boolean}} options - passed to each item's presetItemErrors
 * @returns {{name?: string, items?: string, itemErrors: Array<Record<string, string>>}}
 */
export const presetErrors = (preset, { requireReason = false } = {}) => {
    const items = Array.isArray(preset?.item) ? preset.item : [];
    const errors = { itemErrors: items.map((item) => presetItemErrors(item, { requireReason })) };
    if (isBlank(preset?.name)) errors.name = 'Enter a name';
    if (!items.length) errors.items = 'Add at least one medication';
    return errors;
};

/**
 * Whether presetErrors() reports anything.
 *
 * @param {object} errors - from presetErrors
 * @returns {boolean}
 */
export const hasPresetErrors = (errors) => Boolean(errors?.name || errors?.items || (errors?.itemErrors || []).some((item) => Object.keys(item).length));

const taperTotalDays = (steps) => {
    const spans = presetTaperSpans(steps, 'stop');
    if (!steps.length || spans.some((span) => !span)) return null;
    const chained = rechainTaper(
        steps.map((step) => ({ stepDuration: taperStepDuration(step) })),
        SPAN_ANCHOR,
    );
    const end = chained[chained.length - 1]?.timing?.repeat?.boundsPeriod?.end;
    return end ? differenceInCalendarDays(parseISO(end), parseISO(SPAN_ANCHOR)) + 1 : null;
};

/**
 * One line describing an item for the preset's medication list.
 *
 * @param {object} item
 * @returns {string}
 */
export const describePresetItem = (item) => {
    try {
        const dose = [item?.dose?.value, item?.dose?.unit].filter((part) => !isBlank(part)).join(' ');
        const parts = [dose, item?.route?.text].filter((part) => !isBlank(part));
        const type = item?.administrationType;

        if (type === 'prn') {
            parts.push('PRN');
            if (!isBlank(item?.maxPerDay)) parts.push(`max ${item.maxPerDay}/day`);
            const interval = item?.minimumInterval;
            if (isPositive(interval?.value)) parts.push(`min ${interval.value} ${interval.code ? unitName(interval.code, interval.value) : interval.unit || ''}`.trim());
            return parts.join(' · ');
        }

        if (type === 'taper') {
            const steps = Array.isArray(item?.taperStep) ? item.taperStep : [];
            let summary = `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`;
            if (item?.afterFinal === 'continue') summary += ', final step until cancelled';
            else {
                const days = taperTotalDays(steps);
                if (days) summary += ` over ${days} ${unitName('d', days)}`;
            }
            parts.push('Taper', summary);
            return parts.join(' · ');
        }

        if (isPositive(item?.frequency)) parts.push(`${item.frequency} times ${intervalLabel({ period: item.period, periodUnit: item.periodUnit || 'd' })}`);
        return parts.join(' · ');
    } catch {
        return '';
    }
};
